import React, { useEffect, useState } from "react";
import { Popover } from '@headlessui/react';
import snakeToPascal, { isFloat, round } from "../../../tools";
import dayjs from "dayjs";
import { DATESYSTEM_TO_FORMAT } from "../../../pages/subpages/Settings";
import { useAppStore } from "../../../shared/stores/useAppStore";
import MathJaxComponent from "./MathJax";
import FieldDependenciesPanel from "./reusables/FieldDependenciesPanel";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useScenarioTree } from "../../../shared/hooks/queries/useScenarioTree";


// Removed StyledDataGrid - using custom table with Tailwind

const formatTimeSerieObj = (ts) => {
    const ret = {
        name: ts.name,
        field_id: ts.field_id,
        roundable: ts.roundable,
        is_percentage: ts.is_percentage,
        compute_error: ts.compute_error,
        used_datas: ts.used_datas
    }
    ts.data.map(e => {
        ret[e.date.value + '|' + e.end_of_period.value] = e.value.value
    })
    return ret
}

const renderNameCell = (value) => {
    return (
        <span>{snakeToPascal(value.row[value.field])}</span>
    )
}

const renderDateHeader = (value) => {
    const [bop, eop] = value.split('|');
    return (
        <div className="flex flex-col">
            <span>
                {dayjs(bop).format(DATESYSTEM_TO_FORMAT[useAppStore.getState().userSettings.dateSystem])}
            </span>
            <span>
                {dayjs(eop).format(DATESYSTEM_TO_FORMAT[useAppStore.getState().userSettings.dateSystem])}
            </span>
        </div>
    )
}

const renderCDHeader = () => {
    return (
        <div className="flex flex-col items-center">
            <span>
                Computing
            </span>
            <span>
                Details
            </span>
        </div>
    )
}

const renderCEHeader = () => {
    return (
        <div className="flex flex-col items-center">
            <span>
                Compute
            </span>
            <span>
                Errors
            </span>
        </div>
    )
}


const renderNameHeader = () => {

    return (
        <div className="flex flex-col">
            <span title='Beginning of Period'>
                Start
            </span>
            <span title='End of Period'>
                End
            </span>
        </div>
    )
}

function splitStringIntoChunks(str, chunkSize = 100) {
  const chunks = [];
  let currentChunk = '';
  const words = str.split(' ');

  for (const word of words) {
    if (currentChunk.length + word.length + 1 <= chunkSize) {
      if (currentChunk) {
        currentChunk += ' ';
      }
      currentChunk += word;
    } else {
      chunks.push(currentChunk);
      currentChunk = word;
    }
  }

  if (currentChunk) {
    chunks.push(currentChunk);
  }

  return chunks;
}

const renderComputeErrors = (value) => {
    if (value.row[value.field]) {
        return (
            <Popover className="relative">
                <Popover.Button className="w-[50px] h-[30px] rounded-md bg-red-600 text-white hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 flex items-center justify-center">
                    <span className="material-symbols-outlined text-[20px]">warning</span>
                </Popover.Button>
                <Popover.Panel className="absolute z-10 mt-2 w-[230px] bg-white rounded-lg shadow-lg border border-slate-200">
                    <div className="h-[200px] overflow-y-auto p-4">
                        <span className="material-symbols-outlined text-[35px] text-red-600 block text-center mb-2">warning</span>
                        <p className="text-sm">{value.row[value.field]}</p>
                    </div>
                </Popover.Panel>
            </Popover>
        )
    } else {
        return null;
    }
}

const renderComputingDetails = (value) => {
    const shouldBeDisabled = () => {
        return !value.row[value.field] || value.row[value.field].toLowerCase() == 'undefined' || value.row[value.field].length == 0
    }

    const isDisabled = shouldBeDisabled();

    return (
        <Popover className="relative">
            <Popover.Button
                disabled={isDisabled}
                className={`w-[50px] h-[30px] rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center ${
                    isDisabled
                        ? 'bg-slate-300 cursor-not-allowed'
                        : 'bg-teal-500 hover:bg-teal-400 focus:ring-teal-500'
                }`}
            >
                <span className="material-symbols-outlined text-[20px]">assignment</span>
            </Popover.Button>
            {!isDisabled && (
                <Popover.Panel className="absolute z-10 mt-2 w-fit max-w-2xl bg-white rounded-lg shadow-lg border border-slate-200">
                    <div className="h-[300px] overflow-y-auto">
                        <h3 className="text-center text-xl font-bold p-2">{snakeToPascal(value.row['name'])}</h3>
                        {
                            value.row[value.field] ? value.row[value.field].split('\n').map((str, idx) => {
                                if (str) {
                                    if (str[0] == '|') {
                                        const latexStrings = splitStringIntoChunks(str.substring(1));

                                        return (
                                            <div key={idx}>
                                            {
                                                latexStrings.map((latexString, latexIdx) => (
                                                    <MathJaxComponent key={latexIdx} latex={latexString}/>
                                                ))
                                            }
                                            </div>
                                        )
                                    } else {
                                        return (
                                            <p key={idx} className="px-2">
                                                {str}
                                            </p>
                                        )
                                    }

                                } else {
                                    return <br key={idx}></br>
                                }
                            }) : <div></div>
                        }
                    </div>
                </Popover.Panel>
            )}
        </Popover>
    )

}

const getLonguest = (timeSerieDatas) => {
    let longuest = {};

    timeSerieDatas.map(ts => {
        if (Object.keys(ts).length > Object.keys(longuest).length) {
            longuest = ts
        }
    })

    return longuest;
}

const hasComputeErrors = (fields) => {
    let hasErrors = false;

    Object.values(fields).map((field: any) => {
        if (field.hide) return;
        if (field.compute_error) {
            hasErrors = true;
        }
    });

    return hasErrors
}

type RawGridRow = (string | number)[];

type Cell = {
  value: string | number;
  address: null;
};

type FormattedGridItem = {
  date: Cell;
  end_of_period:Cell;
  value: Cell;
};

function getEndOfMonth(dateString: string): string {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return '';
  }
  const [year, month] = dateString.split('-').map(Number);
  
  if (isNaN(year) || isNaN(month)) {
      return '';
    }

  const lastDayOfMonth = new Date(year, month, 0);

  const yyyy = lastDayOfMonth.getFullYear();
  const mm = String(lastDayOfMonth.getMonth() + 1).padStart(2, '0');
  const dd = String(lastDayOfMonth.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
}


const isValidDate = (str: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(str) && !isNaN(new Date(str).getTime());


const transformData = (data: RawGridRow[]): FormattedGridItem[] => {
  if (!Array.isArray(data) || data.length < 2) return [];

  const header = data[0];
  const totalRow = data[data.length - 1];

  if (!Array.isArray(header) || !Array.isArray(totalRow)) return [];

  const startIndex = header.findIndex((cell) =>
    typeof cell === 'string' && isValidDate(cell)
  );

  if (startIndex === -1) return [];

  const dates = header.slice(startIndex) as string[];
  const values = totalRow.slice(startIndex) as number[];


  const result: FormattedGridItem[] = dates.map((date, i) => ({
    date: { value: date, address: null },
    end_of_period: { value: getEndOfMonth(date), address: null },
    value: { value: values[i] ?? 0, address: null },
  }));

  return result;
};


const getUsedDatas = (usedDatas, currentState) => {
    const result = {};

    Object.keys(currentState['Output'].field_values).map((id) => {
        if (usedDatas.includes(id)) {
            const model_data_value = currentState['Output'].field_values[id]
            let model_data_value_name = ''

            if (model_data_value['display_name']) {
                model_data_value_name = model_data_value['display_name']
            }
            else {
                model_data_value_name = model_data_value['name']
            }
            const model_data_id = model_data_value['model_data_id']
            const model_data = currentState['Output'].tree.items[model_data_id]
            let model_data_name = ''

            if (model_data['display_name']) {
                model_data_name = model_data['display_name']
            }
            else {
                model_data_name = model_data['name']
            }

            if (model_data_value['type'] == 'time_serie') {
                result[model_data_name + ' - ' + model_data_value_name] = model_data_value['value']['value']['data'];
            }

            if (model_data_value['type'] == 'dynamic_grid') {
                const rawData: RawGridRow[] = model_data_value['value']['value']
                const formattedData = transformData(rawData);
                result[model_data_name + ' - ' + model_data_value_name] = formattedData;
            }

            }
        })
    return result
}

export default function OutputTimeSeriesGrid({ fields }) {
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const { state: currentState } = useScenarioTree(scenarioId);
    const timeSerieFields = [];
    
    Object.entries(fields).filter(([name, field]: any) => {
        if (field.hide) return;
        if (field.type == 'time_serie') {
            timeSerieFields.push({
                name: field.display_name ? field.display_name : name,
                field_id: field.id,
                roundable: field.roundable,
                is_percentage: field.is_percentage,
                compute_error: field.compute_error,
                used_datas: getUsedDatas(field.used_datas, currentState),
                data: field.currentValue.value.data
            });
        }
    });
    
    const timeSerieData = timeSerieFields.map(ts => formatTimeSerieObj(ts))

    const renderValueCell = (value) => {
        const rawValue = value.row[value.field];

        if (rawValue == null || rawValue == undefined) {
            return '';
        }

        const valueToFormat = value.row['is_percentage'] && isFloat(rawValue) ? rawValue * 100 : rawValue;
        const suffix = value.row['is_percentage'] && isFloat(rawValue) ? ' %' : '';

        if (isFloat(valueToFormat) && value.row['roundable']) {
            return round(valueToFormat, useAppStore.getState().userSettings.roundPrecision).toLocaleString(useAppStore.getState().userSettings.numberFormattingLocale, {maximumFractionDigits: useAppStore.getState().userSettings.roundPrecision}) + suffix;
        } else if (isFloat(valueToFormat)) {
            return valueToFormat.toLocaleString(useAppStore.getState().userSettings.numberFormattingLocale) + suffix;
        } else {
            return valueToFormat;
        }
    }

    if (timeSerieData.length == 0) {
        return null;
    }

    const longuestTsData = getLonguest(timeSerieData)

    const [depsFieldId, setDepsFieldId] = useState<number | null>(null);
    const [depsFieldName, setDepsFieldName] = useState<string>('');

    const columns = [
        {
          field: 'name',
          headerName: '',
          width: 150,
          pinned: 'left',  // This locks the first column
          renderCell: (value) => renderNameCell(value),
          renderHeader: () => renderNameHeader(),
          disableReorder: true,
          disableColumnMenu: true,
          filterable: false,
          resizable: false,
          sortable: false,
          cellClassName: 'leftmost-column'
        },
        {
          field: 'dependencies',
          headerName: '',
          width: 40,
          renderHeader: () => null,
          renderCell: (value) => (
              <button
                  onClick={() => {
                      setDepsFieldId(value.row.field_id);
                      setDepsFieldName(value.row.name);
                  }}
                  className="p-1 rounded hover:bg-blue-100 text-blue-400 hover:text-blue-600 transition-colors"
                  title="View dependencies"
              >
                  <span className="material-symbols-outlined text-[16px]">info</span>
              </button>
          ),
          disableReorder: true,
          disableColumnMenu: true,
          filterable: false,
          resizable: false,
          sortable: false,
        },
      ];

      if (hasComputeErrors(fields)) {
        columns.push( // @ts-ignore
            {
                field: 'compute_error',
                headerName: 'Compute Errors',
                renderHeader: () => renderCEHeader(),
                renderCell: (value) => renderComputeErrors(value),
                width: 85,
                disableReorder: true,
                disableColumnMenu: true,
                filterable: false,
                resizable: false,
                sortable: false,
            }
        )
      }
    
      if (timeSerieData) {
        // @ts-ignore
        columns.push(...Object.keys(longuestTsData)
            .filter(key => key !== 'name' && key !== 'field_id' && key !== 'compute_error' && key !== 'roundable' && key !== 'is_percentage' && key !== 'used_datas')
            .map(key => ({
                field: key,
                headerName: key,
                renderHeader: () => renderDateHeader(key),
                width: 120,
                // valueFormatter: (value) => formatValue(value),
                display: 'text',
                disableReorder: true,
                disableColumnMenu: true,
                filterable: false,
                resizable: false,
                sortable: false,
                renderCell: (value) => renderValueCell(value),
            })
          )
        )
      }
    
    const formatUsedDatasAsRows = (usedDataDict: Record<string, any[]>) => {
        const rows = [];

        Object.entries(usedDataDict).forEach(([seriesName, dataArray], idx) => {
            const row = { id: idx, name: seriesName };

            dataArray.forEach(entry => {
                const key = `${entry.date.value}|${entry.end_of_period.value}`;
                row[key] = entry.value.value;
            });

            rows.push(row);
        });

        return rows;
    };

    const createEmptyColumn = (width: number, fieldName: string) => ({
        field: fieldName,
        headerName: '',
        width,
        minWidth: width,
        maxWidth: width,
        flex: 0,
        sortable: false,
        filterable: false,
        disableReorder: true,
        disableColumnMenu: true,
        resizable: false,
        renderCell: () => null,
    });

    
    const columnsWithSpacer = [
        columns[0],
        createEmptyColumn(40, 'spacer_deps'),
        createEmptyColumn(50, 'spacer1'),
        createEmptyColumn(95, 'spacer2'),
        ...columns.slice(3),
    ];


    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    const toggleRow = (rowName: string) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(rowName)) {
                newSet.delete(rowName);
            } else {
                newSet.add(rowName);
            }
            return newSet;
        });
    };

    return (
        <div className="mr-2 h-[600px] overflow-auto relative z-0">
            <FieldDependenciesPanel
                isOpen={depsFieldId !== null}
                onClose={() => setDepsFieldId(null)}
                scenarioId={scenarioId}
                fieldId={depsFieldId}
                fieldName={depsFieldName}
            />
            <table className="w-full border-collapse text-sm">
                <thead className="bg-slate-50 sticky top-0 z-20">
                    <tr className="border-b border-slate-200">
                        {columns.map((col, idx) => (
                            <th
                                key={idx}
                                className={`p-2 text-center ${idx === 0 ? 'text-left sticky left-0 z-30 bg-slate-50' : ''}`}
                                style={{ width: col.width }}
                            >
                                {col.renderHeader ? col.renderHeader() : col.headerName}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {timeSerieData.map((row, rowIdx) => {
                        const hasDetails = row.used_datas && Object.keys(row.used_datas).length > 0;
                        const isExpanded = expandedRows.has(row.name);

                        return (
                            <React.Fragment key={row.name}>
                                <tr className="border-b border-slate-200 hover:bg-slate-50">
                                    {columns.map((col, colIdx) => (
                                        <td
                                            key={colIdx}
                                            className={`p-2 text-center ${colIdx === 0 ? 'text-left sticky left-0 z-10 bg-white' : ''}`}
                                        >
                                            {colIdx === 0 && hasDetails && (
                                                <button
                                                    onClick={() => toggleRow(row.name)}
                                                    className="mr-2 text-slate-600 hover:text-slate-900"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">
                                                        {isExpanded ? 'expand_less' : 'expand_more'}
                                                    </span>
                                                </button>
                                            )}
                                            {col.renderCell
                                                ? col.renderCell({ row, value: row[col.field], field: col.field })
                                                : row[col.field]}
                                        </td>
                                    ))}
                                </tr>
                                {isExpanded && hasDetails && (
                                    <tr>
                                        <td colSpan={columns.length} className="bg-slate-50 p-0">
                                            <div className="h-[400px] overflow-x-auto border-t border-slate-200">
                                                <table className="w-full border-collapse">
                                                    <tbody>
                                                        {formatUsedDatasAsRows(row.used_datas).map((detailRow) => (
                                                            <tr key={detailRow.id} className="border-b border-slate-200">
                                                                {columnsWithSpacer.map((col, colIdx) => (
                                                                    <td
                                                                        key={colIdx}
                                                                        className={`p-2 text-center ${colIdx === 0 ? 'text-left' : ''}`}
                                                                        style={{ width: col.width }}
                                                                    >
                                                                        {col.renderCell
                                                                            ? col.renderCell({ row: detailRow, value: detailRow[col.field], field: col.field })
                                                                            : detailRow[col.field] || ''}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    )
}
